import { flexRender, type Row } from '@tanstack/react-table';
import { Fragment, type ReactNode } from 'react';

import { SDSTableCell } from '@repo/sds/components/SDSTable/SDSTableCell';
import { SDSTableRow } from '@repo/sds/components/SDSTable/SDSTableRow';
import type { SDSTableFeatures, TableData } from '@repo/sds/components/SDSTable/types/table.types';
import { getCellDataTestId, getColumnAlign } from '@repo/sds/components/SDSTable/utils/header-utils';

interface SDSTableBodyRowProps<T extends TableData> {
  row: Row<SDSTableFeatures, T>;
  getRowId: (row: T) => string;
  onRowClick: ((row: T) => void) | undefined;
  expandedRowId: string | null | undefined;
  renderExpandedRow: ((row: T) => ReactNode) | undefined;
  columnsCount: number;
}

export function SDSTableBodyRow<T extends TableData>({
  row,
  getRowId,
  onRowClick,
  expandedRowId,
  renderExpandedRow,
  columnsCount,
}: SDSTableBodyRowProps<T>) {
  const rowId = getRowId(row.original);
  const isExpanded = Boolean(renderExpandedRow && expandedRowId === rowId);

  return (
    <Fragment>
      <SDSTableRow onClick={() => onRowClick?.(row.original)} clickable={Boolean(onRowClick)}>
        {row.getVisibleCells().map(cell => (
          <SDSTableCell
            key={cell.id}
            align={getColumnAlign(cell.column.columnDef.meta)}
            data-testid={getCellDataTestId(cell.column.id, cell.column.columnDef.meta)}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </SDSTableCell>
        ))}
      </SDSTableRow>
      {isExpanded && renderExpandedRow ? (
        <SDSTableRow className="SDSTableRow--expanded">
          <SDSTableCell className="SDSTableCell--expanded" colSpan={columnsCount}>
            {renderExpandedRow(row.original)}
          </SDSTableCell>
        </SDSTableRow>
      ) : null}
    </Fragment>
  );
}
